/*!

=========================================================
* Argon Dashboard React - v1.2.4
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import { useEffect, useState } from "react";
import axios from "axios";
// node.js library that concatenates classes (strings)
import classnames from "classnames";
// javascipt plugin for creating charts
import Chart from "chart.js";
// react plugin used to create charts
import { Line, Bar } from "react-chartjs-2";

import { useNavigate } from "react-router-dom";
// reactstrap components
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  NavItem,
  NavLink,
  Nav,
  Progress,
  Table,
  Container,
  Row,
  Col,
} from "reactstrap";

// core components
import {
  chartOptions,
  parseOptions,
  chartExample1,
  chartExample2,
} from "variables/charts.js";
import { startOfWeek } from "date-fns";
import Topholders from "components/Topholders/Topholders.js";
import Header from "components/Headers/Header.js";
import { format, parseISO } from "date-fns";
import SalesChart from "components/SalesChart/SalesChart";
import CartPie from "components/ChartPie/ChartPie";
import Toplocation from "components/TopLocation/Toplocation";
import Topsources from "components/TopSource/TopSources";
const Index = (props) => {
  const [activeNav, setActiveNav] = useState(1);
  const [chartExample1Data, setChartExample1Data] = useState("data1");
  const [transactions, setTransactions] = useState([]);
  const [users, setUsers] = useState([]);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem("token"); // or your token source
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/users`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        const data = response.data.users || [];
        setUsers(data);

        const stats = calculateMonthlyStats(data);
        setMonthlyStats(stats);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchUsers();
  }, []);

  const calculateMonthlyStats = (users) => {
    const monthMap = {}; // To store user counts by month
    let totalUsers = 0;

    // Group users by month
    users.forEach((user) => {
      const month = format(parseISO(user.createdAt), "MMMM yyyy");
      monthMap[month] = (monthMap[month] || 0) + 1;
      totalUsers++;
    });

    // Calculate stats with percentages
    return Object.entries(monthMap).map(([month, count]) => ({
      month,
      count,
      percentage: ((count / totalUsers) * 100).toFixed(2),
    }));
  };
  useEffect(() => {
    // Fetch data from the API when the component mounts
    axios
      .get(`${process.env.REACT_APP_API_URL}/orders`)
      .then((response) => {
        // Assuming the response contains an array of transactions
        setTransactions(response.data.orders.reverse().slice(0, 5));
        // Get only the first 5 transactions
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  }, []);
  if (window.Chart) {
    parseOptions(Chart, chartOptions());
  }

  const [totalOrdersData, setTotalOrdersData] = useState({
    monthly: [],
    weekly: [],
  });

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/orders`,
        );
        const orders = response.data.orders || [];

        const { monthlyOrders, weeklyOrders } = calculateStats(orders);

        setTotalOrdersData({ monthly: monthlyOrders, weekly: weeklyOrders });
      } catch (error) {
        console.error("Error fetching orders:", error);
      }
    };

    fetchOrders();
  }, []);

  // Function to calculate statistics
  const calculateStats = (orders) => {
    const monthlyOrders = {};
    const weeklyOrders = {};

    // Helper function to get the week of the year
    const getWeek = (date) => {
      const start = startOfWeek(date, { weekStartsOn: 1 });
      return format(start, "yyyy-MM-dd");
    };

    // Iterate through orders to aggregate data
    orders.forEach((order) => {
      const createdAt = parseISO(order.createdAt);
      const month = format(createdAt, "MMMM yyyy");
      const week = getWeek(createdAt);

      // Aggregating total orders by month and week
      monthlyOrders[month] = (monthlyOrders[month] || 0) + 1;
      weeklyOrders[week] = (weeklyOrders[week] || 0) + 1;
    });

    // Convert to arrays for chart usage
    const monthlyOrdersArray = Object.entries(monthlyOrders).map(
      ([month, orders]) => ({ month, orders }),
    );
    const weeklyOrdersArray = Object.entries(weeklyOrders).map(
      ([week, orders]) => ({ week, orders }),
    );

    return {
      monthlyOrders: monthlyOrdersArray,
      weeklyOrders: weeklyOrdersArray,
    };
  };

  // Prepare data for Bar chart based on selected navigation (Month or Week)
  const chartDataTotalOrders =
    activeNav === 1 ? totalOrdersData.monthly : totalOrdersData.weekly;

  const totalOrdersLabels = chartDataTotalOrders.map(
    (item) => item.month || item.week,
  );
  const totalOrdersValues = chartDataTotalOrders.map((item) => item.orders);

  const [tokenSalesData, setTokenSalesData] = useState({}); // Renamed from chartData
  const [selectedTimeFrame, setSelectedTimeFrame] = useState(1); // Renamed from activeNav
  const [isLoading, setIsLoading] = useState(true); // Renamed from loading

  const toggleNavs = (e, index) => {
    e.preventDefault();
    setSelectedTimeFrame(index); // Updated to use selectedTimeFrame
  };

  useEffect(() => {
    const fetchTokenSales = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/orders`);
        const data = await response.json();

        // Process the data to group by month
        const monthlyTokens = processTokenSalesByMonth(data.orders);

        // Set the chart data
        setTokenSalesData({
          labels: monthlyTokens.map((item) => item.month), // Labels for months
          datasets: [
            {
              label: "Tokens Sold",
              data: monthlyTokens.map((item) => item.totalTokens), // Tokens sold per month
              fill: false,
              backgroundColor: "#FFB000", // Bar color
              borderColor: "#FFB000", // Line color
              tension: 0.4,
            },
          ],
        });
        setIsLoading(false); // Updated to use isLoading
      } catch (error) {
        console.error("Error fetching token sales:", error);
        setIsLoading(false); // Updated to use isLoading
      }
    };

    fetchTokenSales();
  }, []);

  // Process the orders to sum tokens per month
  const processTokenSalesByMonth = (orders) => {
    const tokensByMonth = {};

    orders.forEach((order) => {
      const orderDate = new Date(order.created_at);
      const yearMonth = `${orderDate.getFullYear()}-${orderDate.getMonth() + 1}`;

      if (tokensByMonth[yearMonth]) {
        tokensByMonth[yearMonth] += parseFloat(order.tokens);
      } else {
        tokensByMonth[yearMonth] = parseFloat(order.tokens);
      }
    });

    return Object.keys(tokensByMonth).map((month) => ({
      month,
      totalTokens: tokensByMonth[month],
    }));
  };

  return (
    <>
      <Header />
      {/* Page content */}
      <Container
        style={{ backgroundColor: "#22222", paddingBottom: "100px" }}
        className="mt--7"
        fluid
      >
        <Row>
          <Col className="mb-5 mb-xl-0" xl="8">
            <Card
              className="shadow"
              style={{
                background: "black",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
              }}
            >
              <SalesChart />
            </Card>
          </Col>
          <Col xl="4">
            <Card
              className="shadow"
              style={{
                background: "black",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
              }}
            >
              <CardHeader className="bg-transparent">
                <Row className="align-items-center">
                  <div className="col">
                    <h6
                      className="text-uppercase text-muted ls-1 mb-1"
                      style={{ color: "#FFB000" }}
                    >
                      Performance
                    </h6>
                    <h2 className="mb-0" style={{ color: "#FFB000" }}>
                      Total Orders
                    </h2>
                  </div>
                </Row>
              </CardHeader>
              <CardBody>
                <div className="chart">
                  <Bar
                    data={{
                      labels: totalOrdersLabels,
                      datasets: [
                        {
                          label: "Total Orders",
                          data: totalOrdersValues,
                          backgroundColor: "#FFB000",
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        x: {
                          ticks: { color: "#FFB000" },
                        },
                        y: {
                          ticks: { color: "#FFB000" },
                        },
                      },
                    }}
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="mt-5">
          <Col className="mb-5 mb-xl-0" xl="6">
            <Card
              className="shadow"
              style={{
                background: "black",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
              }}
            >
              <CartPie />
            </Card>
          </Col>

          <Col className="mb-5 mb-xl-0" xl="6">
            <Card
              className="shadow"
              style={{
                background: "black",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
              }}
            >
              <Topholders />
            </Card>
          </Col>
        </Row>
        <br />
        <br />

        {/* <Row>
  <Col className="mb-5 mb-xl-0" xl="8">
  <Card className="shadow" style={{ background: "black", boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)" }}>
      <Toplocation/>
  </Card>
</Col>
  <Col className="mb-5 mb-xl-0" xl="4">
  <Card className="shadow" style={{ background: "black", boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)" }}>
      <Topsources/>
  </Card>
</Col>


    </Row> */}
        {/* <br/> */}
        {/* <br/> */}
        {/* <br/> */}
        <br />
        <Row>
          <Col className="mb-5 mb-xl-0" xl="6">
            <Card
              className="shadow"
              style={{
                background: "black",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
              }}
            >
              <CardHeader className="border-0" style={{ background: "black" }}>
                <Row className="align-items-center">
                  <div className="col">
                    <h3 className="mb-0" style={{ color: "#FFB000" }}>
                      Recent Transaction
                    </h3>
                  </div>
                  <div className="col text-right">
                    <Button
                      color="warning" // Yellow button
                      href="#pablo"
                      onClick={() => navigate("/admin/transactionlist")}
                      size="sm"
                      style={{
                        backgroundColor: "#FFB000",
                        borderColor: "#FFB000",
                        color: "#000",
                      }} // Yellow button with black text
                    >
                      See all
                    </Button>
                  </div>
                </Row>
              </CardHeader>
              <Table
                className="align-items-center table-flush"
                responsive
                style={{ border: "none" }}
              >
                {" "}
                {/* No borders */}
                <thead className="thead-light" style={{ background: "black" }}>
                  <tr style={{ background: "black" }}>
                    <th
                      scope="col"
                      style={{
                        color: "#FFB000",
                        background: "black",
                        border: "none",
                        fontSize: "18px",
                      }}
                    >
                      Transaction ID
                    </th>
                    <th
                      scope="col"
                      style={{
                        color: "#FFB000",
                        background: "black",
                        border: "none",
                        fontSize: "18px",
                      }}
                    >
                      Tokens
                    </th>
                    <th
                      scope="col"
                      style={{
                        color: "#FFB000",
                        background: "black",
                        border: "none",
                        fontSize: "18px",
                      }}
                    >
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction, index) => (
                    <tr key={index} style={{ border: "none" }}>
                      <th
                        scope="row"
                        style={{
                          color: "#FFB000",
                          border: "none",
                          fontSize: "16px",
                        }}
                      >
                        {transaction.orderId} <br />
                        <span style={{ color: "white", fontSize: "14px" }}>
                          {transaction.createdAt}
                        </span>
                      </th>
                      <td
                        style={{
                          border: "none",
                          color: "grey",
                          fontSize: "16px",
                        }}
                      >
                        {transaction.amountWagr} <br />
                        <span style={{ color: "white", fontSize: "14px" }}>
                          {transaction.currency}
                        </span>
                      </td>
                      <td
                        style={{
                          border: "none",
                          color: "white",
                          fontSize: "16px",
                        }}
                      >
                        {transaction.status}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          </Col>
          <Col xl="6">
            <Card
              className="shadow"
              style={{
                background: "black",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
              }}
            >
              <CardHeader className="border-0" style={{ background: "black" }}>
                <Row className="align-items-center">
                  <div className="col">
                    <h3 className="mb-0" style={{ color: "#FFB000" }}>
                      Registration Statistic
                    </h3>
                  </div>
                  <div className="col text-right">
                    <Button
                      color="warning" // Yellow button
                      href="#pablo"
                      onClick={() => navigate("/admin/user-list")}
                      size="sm"
                      style={{
                        backgroundColor: "#FFB000",
                        borderColor: "#FFB000",
                        color: "#000",
                      }} // Yellow button with black text
                    >
                      See all
                    </Button>
                  </div>
                </Row>
              </CardHeader>
              <Table
                style={{ background: "black" }}
                className="align-items-center table-flush"
                responsive
              >
                {" "}
                {/* No borders */}
                <thead style={{ background: "black" }} className="">
                  <tr style={{ background: "black" }}>
                    <th scope="col" style={{ color: "#FFB000" }}>
                      Month Users
                    </th>
                  </tr>
                </thead>
                <table className="table align-items-center table-flush">
                  <tbody>
                    {monthlyStats.length > 0 ? (
                      monthlyStats.map((stat, index) => (
                        <tr key={index}>
                          <th scope="row" style={{ color: "#FFB000" }}>
                            {stat.month}
                          </th>
                          <td>{stat.count}</td>
                          <td>
                            <div className="d-flex align-items-center">
                              <span
                                className="mr-1"
                                style={{ color: "#FFB000" }}
                              >
                                {stat.percentage}%
                              </span>
                              <div style={{ width: "50%" }}>
                                <Progress
                                  max="100"
                                  value={stat.percentage}
                                  barClassName="bg-gradient-success"
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center text-white">
                          Loading statistics...
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </Table>
            </Card>
          </Col>
        </Row>
        <br />
        <br />
      </Container>
    </>
  );
};

export default Index;
